import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useState,
} from "react";
import Select, {MultiValue } from "react-select";
import useForm from "../../hooks/useForm";
import {
  initialFabricacion,
  propsFabricacion,
} from "../../utils/formsInitialsStates";
import { useDoneFetchActions } from "../../hooks/useDoneFetchActions";
import { getProductos, getRecetasRegistro, getMaterialesByRecetaIds, getRecetasByMaterialIds } from "../../services/fabricaServices";


interface Props {
  handleSubmit?: (values: propsFabricacion) => void;
  data?: propsFabricacion;
  on?: string;
}

export const RegistroFabriForm = forwardRef(({ handleSubmit, data }: Props, ref) => {
  const doneFetchActions = useDoneFetchActions();
  const { handleInputChange, formValues, resetForm, changeMulti, validateForm } = useForm<propsFabricacion>(initialFabricacion);
  const form = useRef<HTMLFormElement>(null);

  const [materiales, setMateriales] = useState<{ id: number; nombre: string }[]>([]);
  const [recetas, setRecetas] = useState<{ id: number; receta: string; descripcion: string | null }[]>([]);
  const [selectedMateriales, setSelectedMateriales] = useState<{ value: number; label: string }[]>([]);
  const [selectedRecetas, setSelectedRecetas] = useState<{ value: number; label: string }[]>([]);

  useEffect(() => {
    doneFetchActions(getProductos()).then((resp) => {
      setMateriales(resp);
    });
    
    doneFetchActions(getRecetasRegistro()).then((resp) => {
      setRecetas(resp);
    });
  }, [doneFetchActions]);

  useEffect(() => {
    if (selectedMateriales.length > 0) {
      const materialIds = selectedMateriales.map(m => m.value);
      doneFetchActions(getRecetasByMaterialIds(materialIds)).then((resp) => {
        setRecetas(resp);
      });
    } else {
      doneFetchActions(getRecetasRegistro()).then((resp) => {
        setRecetas(resp);
      });
    }
  }, [selectedMateriales, doneFetchActions]);
  
  useEffect(() => {
    if (selectedRecetas.length > 0) {
      const recetaIds = selectedRecetas.map(r => r.value);
      doneFetchActions(getMaterialesByRecetaIds(recetaIds)).then((resp) => {
        setMateriales(resp);
      });
    } else {
      doneFetchActions(getProductos()).then((resp) => {
        setMateriales(resp);
      });
    }
  }, [selectedRecetas, doneFetchActions]);

  useEffect(() => {
    if (data) {
      changeMulti({
        id: data.id,
        fecha_registro: data.fecha_registro,
        comentario: data.comentario,
        datos_receta: data.fabricaReceta?.map(fr => ({
          fabricacion_id: fr.fabricacion_id,
          receta_id: fr.receta.id,
          cantidad_producida: fr.cantidad_producida,
          unidad_medida_producto: fr.unidad_medida_producto
        })) || [],
        datos_materiales: data.fabricaReceta?.[0]?.extraccion?.extraccionMat?.map(em => ({
          extraccion_id: em.extraccion_id,
          material_id: em.material_id,
          cantidad: em.cantidad
        })) || []
      });

      const selectedRecetas = data.fabricaReceta?.map(fr => ({
        value: fr.receta.id,
        label: fr.receta.receta
      })) || [];
      setSelectedRecetas(selectedRecetas);

      const selectedMateriales = data.fabricaReceta?.[0]?.extraccion?.extraccionMat?.map(em => ({
        value: em.materialExt.id,
        label: em.materialExt.nombre
      })) || [];
      setSelectedMateriales(selectedMateriales);
    }
  }, [data, changeMulti]);

  const handleMaterialChange = (newValue: MultiValue<{ value: number; label: string }>) => {
    setSelectedMateriales(newValue as { value: number; label: string }[]);
    const updatedMateriales = newValue.map(option => ({
      extraccion_id: 0,
      material_id: option.value,
      cantidad: ''
    }));
    changeMulti({ datos_materiales: updatedMateriales });
  };

  const handleRecetaChange = (newValue: MultiValue<{ value: number; label: string }>) => {
    setSelectedRecetas(newValue as { value: number; label: string }[]);
    const updatedRecetas = newValue.map(option => ({
      fabricacion_id: 0,
      receta_id: option.value,
      cantidad_producida: 0,
      unidad_medida_producto: '',
    }));
    changeMulti({ datos_receta: updatedRecetas });
  };

  const handleMaterialQuantityChange = (index: number, cantidad: string) => {
    if (formValues.datos_materiales && formValues.datos_materiales[index]) {
      const updatedMateriales = [...formValues.datos_materiales];
      updatedMateriales[index].cantidad = cantidad;
      changeMulti({ datos_materiales: updatedMateriales });
    }
  };

  const handleQuantityChange = (index: number, cantidad_producida: number) => {
    if (formValues.datos_receta && formValues.datos_receta[index]) {
      const updatedRecetas = [...formValues.datos_receta];
      updatedRecetas[index].cantidad_producida = cantidad_producida;
      changeMulti({ datos_receta: updatedRecetas });
    }
  };

  const handleUnitChange = (index: number, unidad_medida_producto: string) => {
    if (formValues.datos_receta && formValues.datos_receta[index]) {
      const updatedRecetas = [...formValues.datos_receta];
      updatedRecetas[index].unidad_medida_producto = unidad_medida_producto;
      changeMulti({ datos_receta: updatedRecetas });
    }
  };

  useImperativeHandle(ref, () => ({
    handleClick: () => {
      form.current?.requestSubmit();
      if (form.current && validateForm(form.current.elements)) {
        return formValues;
      }
    },
    resetForm: () => {
      resetForm();
    },
  }));

  const submitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit && handleSubmit(formValues);
  };


  return (
    <form className="p-3" ref={form} autoComplete="off" onSubmit={submitForm} noValidate>
      <div className="row">
        <div className="col-md-12">
          <div className="form-floating mb-3">
            <input
              type="date"
              className="form-control"
              id="fecha_registro"
              name="fecha_registro"
              value={formValues.fecha_registro}
              onChange={handleInputChange}
            />
            <label htmlFor="fecha_registro">Fecha</label>
          </div>
        </div>
      </div>


      <div className="row">
        <div className="col-md-12">
          <label htmlFor="materiales">Material(es)</label>
          <div className="form-floating mb-4">
            <Select
              id="materiales"
              name="materiales"
              value={selectedMateriales}
              isMulti
              closeMenuOnSelect={true}
              options={materiales.map((m) => ({ value: m.id, label: m.nombre }))}
              onChange={handleMaterialChange}
            />
          </div>
        </div>
      </div>
      {selectedMateriales.length > 0 && (
        <div className="row">
          {selectedMateriales.map((material, index) => (
            <div className="col-md-12 mb-3" key={material.value}>
              <div className="input-group">
                <div className="input-group-prepend w-50">
                  <label className="input-group-text" htmlFor={`material_id_${index}`}>
                    Material
                  </label>
                  <input
                    type="text"
                    id={`material_id_${index}`}
                    className="form-control"
                    value={material.label}
                    readOnly
                  />
                </div>
                <div className="input-group-prepend w-50">
                  <label className="input-group-text" htmlFor={`cantidad_${index}`}>
                    Cantidad kg/gr - lts/mlts
                  </label>
                  <input
                    type="number"
                    id={`cantidad_${index}`}
                    className="form-control"
                    value={formValues.datos_materiales[index]?.cantidad || ''}
                    onChange={(e) => handleMaterialQuantityChange(index, e.target.value)}
                    placeholder="kg/gr - lts/mlts"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}


      <div className="row">
        <div className="col-md-12">
          <label htmlFor="receta_id">Receta(s)</label>
          <div className="form-floating mb-4">
            <Select
              id="receta_id"
              name="receta_id"
              value={selectedRecetas}
              isMulti
              closeMenuOnSelect={false}
              options={recetas.map((r) => ({ value: r.id, label: r.receta }))}
              onChange={handleRecetaChange}
            />
          </div>
        </div>
      </div>
      {selectedRecetas.length > 0 && (
        <div className="row">
          {formValues.datos_receta.map((receta, index) => (
            <div className="col-md-12 mb-3" key={receta.receta_id}>
              <div className="input-group">
                <div className="input-group-prepend w-50">
                  <label className="input-group-text" htmlFor={`receta_id_${index}`}>
                    Receta
                  </label>
                  <input
                    type="text"
                    id={`receta_id_${index}`}
                    className="form-control"
                    value={selectedRecetas.find(r => r.value === receta.receta_id)?.label || ''}
                    readOnly
                  />
                </div>
                <div className="input-group-prepend w-25">
                  <label className="input-group-text" htmlFor={`cantidad_producida_${index}`}>
                    Cantidad
                  </label>
                  <input
                    type="number"
                    id={`cantidad_producida_${index}`}
                    className="form-control"
                    value={receta.cantidad_producida}
                    onChange={(e) => handleQuantityChange(index, Number(e.target.value))}
                    placeholder="Cantidad"
                  />
                </div>
                <div className="input-group-prepend w-25">
                  <label className="input-group-text" htmlFor={`unidad_medida_producto_${index}`}>
                    Medida
                  </label>
                  <select
                    className="form-select"
                    id={`unidad_medida_producto_${index}`}
                    name="unidad_medida_producto"
                    value={receta.unidad_medida_producto}
                    onChange={(e) => handleUnitChange(index, e.target.value)}
                    required
                  >
                    <option value="" disabled>
                      Selección
                    </option>
                    <option value="lts">Litros</option>
                    <option value="kg">Kilos</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="row">
        <div className="col-md-12">
          <div className="form-floating mb-3">
            <textarea
              className="form-control"
              id="comentario"
              name="comentario"
              value={formValues.comentario}
              onChange={handleInputChange}
            ></textarea>
            <label htmlFor="comentario">Comentario</label>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-8">
          <div className="form-floating mb-3">
            <select className="form-select" id="estado" name="estado" value={Number(formValues.estado)} onChange={handleInputChange} required>
              <option value="" disabled>Seleccione un estado</option>
              <option value={1}>Habilitado</option>
              <option value={0}>Inhabilitado</option>
            </select>
            <label htmlFor="estado">Habilitar/Inhabilitar</label>
          </div>
        </div>
        </div>
    </form>
  );
});
